import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface QuizQuestion {
  id: string;
  quiz_id: string;
  question_text: string;
  points: number;
  sequence_order: number;
  options: QuizOption[];
}

export interface QuizOption {
  id: string;
  question_id: string;
  option_text: string;
  is_correct: boolean;
  sequence_order: number;
}

export interface Quiz {
  id: string;
  module_id: string;
  title: string;
  description: string | null;
  passing_score: number;
  time_limit_minutes: number | null;
  is_published: boolean;
  created_at: string;
  questions?: QuizQuestion[];
}

export const useModuleQuizzes = (moduleId: string | undefined) => {
  return useQuery({
    queryKey: ["quizzes", moduleId],
    queryFn: async () => {
      if (!moduleId) return [];
      const { data, error } = await supabase
        .from("quizzes")
        .select("*")
        .eq("module_id", moduleId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as Quiz[];
    },
    enabled: !!moduleId,
  });
};

export const useQuizWithQuestions = (quizId: string | undefined) => {
  return useQuery({
    queryKey: ["quiz", quizId],
    queryFn: async () => {
      if (!quizId) return null;
      
      // Get quiz
      const { data: quiz, error: quizError } = await supabase
        .from("quizzes")
        .select("*")
        .eq("id", quizId)
        .single();
      if (quizError) throw quizError;

      // Get questions
      const { data: questions, error: questionsError } = await supabase
        .from("quiz_questions")
        .select("*")
        .eq("quiz_id", quizId)
        .order("sequence_order", { ascending: true });
      if (questionsError) throw questionsError;

      // Get options for all questions
      const questionIds = questions.map(q => q.id);
      const { data: options, error: optionsError } = await supabase
        .from("quiz_question_options")
        .select("*")
        .in("question_id", questionIds)
        .order("sequence_order", { ascending: true });
      if (optionsError) throw optionsError;

      // Combine questions with their options
      const questionsWithOptions = questions.map(q => ({
        ...q,
        options: options.filter(o => o.question_id === q.id)
      }));

      return { ...quiz, questions: questionsWithOptions } as Quiz;
    },
    enabled: !!quizId,
  });
};

export const useCreateQuiz = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { module_id: string; title: string; description?: string; passing_score?: number }) => {
      const { data: quiz, error } = await supabase
        .from("quizzes")
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return quiz;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["quizzes", variables.module_id] });
      toast({ title: "Quiz created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create quiz", description: error.message, variant: "destructive" });
    },
  });
};

export const useUpdateQuiz = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Quiz> & { id: string }) => {
      const { error } = await supabase
        .from("quizzes")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quizzes"] });
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
      toast({ title: "Quiz updated successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update quiz", description: error.message, variant: "destructive" });
    },
  });
};

export const useDeleteQuiz = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (quizId: string) => {
      const { error } = await supabase
        .from("quizzes")
        .delete()
        .eq("id", quizId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quizzes"] });
      toast({ title: "Quiz deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete quiz", description: error.message, variant: "destructive" });
    },
  });
};

export const useAddQuizQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { 
      quiz_id: string; 
      question_text: string; 
      points: number;
      options: { option_text: string; is_correct: boolean }[];
    }) => {
      // Get the next sequence order
      const { data: existingQuestions } = await supabase
        .from("quiz_questions")
        .select("sequence_order")
        .eq("quiz_id", data.quiz_id)
        .order("sequence_order", { ascending: false })
        .limit(1);
      
      const nextOrder = existingQuestions && existingQuestions.length > 0 
        ? existingQuestions[0].sequence_order + 1 
        : 1;

      // Create the question
      const { data: question, error: questionError } = await supabase
        .from("quiz_questions")
        .insert({
          quiz_id: data.quiz_id,
          question_text: data.question_text,
          points: data.points,
          sequence_order: nextOrder,
        })
        .select()
        .single();
      if (questionError) throw questionError;

      // Create the options
      const optionsToInsert = data.options.map((opt, idx) => ({
        question_id: question.id,
        option_text: opt.option_text,
        is_correct: opt.is_correct,
        sequence_order: idx + 1,
      }));

      const { error: optionsError } = await supabase
        .from("quiz_question_options")
        .insert(optionsToInsert);
      if (optionsError) throw optionsError;

      return question;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
      toast({ title: "Question added successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to add question", description: error.message, variant: "destructive" });
    },
  });
};

export const useDeleteQuizQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (questionId: string) => {
      const { error } = await supabase
        .from("quiz_questions")
        .delete()
        .eq("id", questionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
      toast({ title: "Question deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete question", description: error.message, variant: "destructive" });
    },
  });
};

export const useUpdateQuizQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { 
      id: string; 
      question_text?: string; 
      points?: number;
    }) => {
      const { id, ...updateData } = data;
      const { error } = await supabase
        .from("quiz_questions")
        .update(updateData)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
      toast({ title: "Question updated successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update question", description: error.message, variant: "destructive" });
    },
  });
};

export const useUpdateQuizOption = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { 
      id: string; 
      option_text?: string; 
      is_correct?: boolean;
    }) => {
      const { id, ...updateData } = data;
      
      const { error } = await supabase
        .from("quiz_question_options")
        .update(updateData)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update option", description: error.message, variant: "destructive" });
    },
  });
};
